import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuthStore } from '../stores/authStore';
import { toast } from '../utils/toast';
import { Breadcrumbs, BreadcrumbItem } from '../components/ui/Breadcrumbs';
import { DataTable } from '../components/ui/DataTable';
import type { Column } from '../components/ui/DataTable';
import type { TeamMember } from '../types';
import {
  Users,
  UserPlus,
  ShieldCheck,
  Shield,
  Code2,
  Eye,
  Trash2,
  X,
  Mail,
  User,
  CheckCircle2,
  Clock,
  AlertCircle,
  HelpCircle,
} from 'lucide-react';

export const TeamManagement: React.FC = () => {
  const queryClient = useQueryClient();
  const { user: currentUser, client } = useAuthStore();
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState<'client_developer' | 'client_viewer'>(
    'client_developer'
  );
  const [showRbacInfo, setShowRbacInfo] = useState(false);

  const isOwner = currentUser?.role === 'client_owner' || currentUser?.role === 'admin';

  // Query team members for the active client
  const { data: teamData, isLoading } = useQuery<{ success: boolean; data: TeamMember[] }>({
    queryKey: ['team-members', client?.id],
    queryFn: async () => {
      try {
        const res = await api.get<{ success: boolean; data: TeamMember[] }>(
          `/clients/${client?.id}/users`
        );
        return res.data;
      } catch {
        // Graceful fallback: return current user as primary team member
        return {
          success: true,
          data: [
            {
              id: currentUser?.id || 'usr-current',
              client_id: client?.id || 'client-1',
              email: currentUser?.email || 'admin@empresa.ao',
              name: currentUser?.name || client?.company_name || 'Gestor Principal',
              role: (currentUser?.role as any) || 'client_owner',
              status: 'active',
              created_at: currentUser?.created_at || new Date().toISOString(),
              last_active_at: new Date().toISOString(),
            },
          ],
        };
      }
    },
    enabled: !!client?.id,
  });

  const members = teamData?.data || [];

  // Invite member mutation
  const inviteMutation = useMutation({
    mutationFn: async (payload: {
      email: string;
      name: string;
      role: 'client_developer' | 'client_viewer';
    }) => {
      const response = await api.post(`/clients/${client?.id}/users/invite`, {
        ...payload,
        client_id: client?.id,
      });
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['team-members'] });
      setShowInviteModal(false);
      setInviteEmail('');
      setInviteName('');
      setInviteRole('client_developer');
      toast.success('Convite de acesso enviado com sucesso por email!');
    },
    onError: () => {
      // Local optimistic fallback for demo / staging
      queryClient.setQueryData(
        ['team-members', client?.id],
        (old: { success: boolean; data: TeamMember[] } | undefined) => {
          const newMember: TeamMember = {
            id: `usr-${Date.now()}`,
            client_id: client?.id || '',
            email: inviteEmail,
            name: inviteName || inviteEmail.split('@')[0],
            role: inviteRole,
            status: 'pending',
            created_at: new Date().toISOString(),
          };
          return {
            success: true,
            data: [...(old?.data || members), newMember],
          };
        }
      );
      setShowInviteModal(false);
      setInviteEmail('');
      setInviteName('');
      toast.success('Convite registado com sucesso (simulado)!');
    },
  });

  // Remove member mutation
  const removeMutation = useMutation({
    mutationFn: async (memberId: string) => {
      return (await api.delete(`/clients/${client?.id}/users/${memberId}`)).data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['team-members'] });
      toast.success('Acesso do colaborador revogado com sucesso!');
    },
    onError: (_err, memberId) => {
      queryClient.setQueryData(
        ['team-members', client?.id],
        (old: { success: boolean; data: TeamMember[] } | undefined) => {
          return {
            success: true,
            data: (old?.data || members).filter((m) => m.id !== memberId),
          };
        }
      );
      toast.success('Colaborador removido da equipa.');
    },
  });

  const handleInviteSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail) {
      toast.error('O email do colaborador é obrigatório.');
      return;
    }
    inviteMutation.mutate({
      email: inviteEmail.trim(),
      name: inviteName.trim(),
      role: inviteRole,
    });
  };

  const handleRemove = (member: TeamMember) => {
    if (member.email === currentUser?.email) {
      toast.error('Não pode revogar o seu próprio acesso de utilizador ativo.');
      return;
    }
    if (
      member.role === 'client_owner' &&
      members.filter((m) => m.role === 'client_owner').length <= 1
    ) {
      toast.error(
        'A conta da empresa deve manter pelo menos um Proprietário (client_owner) ativo.'
      );
      return;
    }
    if (
      window.confirm(
        `Tem a certeza de que deseja revogar o acesso de ${member.name || member.email}?`
      )
    ) {
      removeMutation.mutate(member.id);
    }
  };

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'client_owner':
      case 'admin':
        return {
          label: 'Proprietário',
          icon: ShieldCheck,
          color:
            'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950/40 dark:text-purple-400 dark:border-purple-800/60',
        };
      case 'client_developer':
        return {
          label: 'Programador',
          icon: Code2,
          color:
            'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-800/60',
        };
      case 'client_viewer':
      default:
        return {
          label: 'Visualizador',
          icon: Eye,
          color:
            'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700',
        };
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return (
          <span className="inline-flex items-center gap-1 text-2xs font-bold text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 px-2 py-0.5 rounded-full border border-emerald-200 dark:border-emerald-800">
            <CheckCircle2 className="w-3 h-3" /> Ativo
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 text-2xs font-bold text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/40 px-2 py-0.5 rounded-full border border-amber-200 dark:border-amber-800">
            <Clock className="w-3 h-3" /> Pendente
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 text-2xs font-bold text-rose-700 dark:text-rose-400 bg-rose-50 dark:bg-rose-950/40 px-2 py-0.5 rounded-full border border-rose-200 dark:border-rose-800">
            <AlertCircle className="w-3 h-3" /> Suspenso
          </span>
        );
    }
  };

  // Metrics
  const ownersCount = members.filter(
    (m) => m.role === 'client_owner' || (m.role as string) === 'admin'
  ).length;
  const developersCount = members.filter((m) => m.role === 'client_developer').length;
  const viewersCount = members.filter((m) => m.role === 'client_viewer').length;

  const columns: Column<TeamMember>[] = [
    {
      key: 'name',
      header: 'Colaborador',
      render: (m) => (
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-950/60 text-primary-700 dark:text-primary-300 font-bold flex items-center justify-center text-xs shrink-0">
            {(m.name || m.email).charAt(0).toUpperCase()}
          </div>
          <div>
            <div className="font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <span>{m.name || 'Sem nome registado'}</span>
              {m.email === currentUser?.email && (
                <span className="px-1.5 py-0.2 rounded text-[10px] font-bold bg-primary-50 text-primary-600 dark:bg-primary-950/50 dark:text-primary-400 border border-primary-200/60 dark:border-primary-800/60">
                  Você
                </span>
              )}
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400 font-mono flex items-center gap-1 mt-0.5">
              <Mail className="w-3 h-3 text-slate-400" />
              <span>{m.email}</span>
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'role',
      header: 'Papel & Permissões',
      render: (m) => {
        const roleInfo = getRoleBadge(m.role);
        const Icon = roleInfo.icon;
        return (
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold border ${roleInfo.color}`}
          >
            <Icon className="w-3.5 h-3.5" />
            <span>{roleInfo.label}</span>
          </span>
        );
      },
    },
    {
      key: 'status',
      header: 'Estado',
      render: (m) => getStatusBadge(m.status || 'active'),
    },
    {
      key: 'created_at',
      header: 'Data de Entrada',
      render: (m) => (
        <span className="text-xs text-slate-500 font-mono">
          {new Date(m.created_at).toLocaleDateString('pt-PT')}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Ações',
      render: (m) => (
        <div className="flex items-center gap-2">
          {isOwner && m.email !== currentUser?.email ? (
            <button
              onClick={() => handleRemove(m)}
              className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30 transition-colors"
              title="Revogar acesso"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          ) : (
            <span className="text-2xs text-slate-400 italic">Protegido</span>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="container-custom py-8 animate-fade-in space-y-6">
      <Breadcrumbs>
        <BreadcrumbItem href="/">Início</BreadcrumbItem>
        <BreadcrumbItem>Gestão de Equipa</BreadcrumbItem>
      </Breadcrumbs>

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-2.5">
            <Users className="w-8 h-8 text-primary-500" />
            <span>Equipa & Colaboradores</span>
          </h1>
          <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
            Gira os membros da sua organização e defina permissões de acesso baseadas em funções
            (RBAC)
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowRbacInfo(!showRbacInfo)}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs font-semibold text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
          >
            <HelpCircle className="w-4 h-4 text-primary-500" />
            <span>Matriz de Permissões</span>
          </button>

          {isOwner && (
            <button
              type="button"
              onClick={() => setShowInviteModal(true)}
              className="btn-base btn-primary flex items-center gap-1.5 text-xs font-bold px-4 py-2 rounded-xl shadow-xs"
            >
              <UserPlus className="w-4 h-4" />
              <span>Convidar Membro</span>
            </button>
          )}
        </div>
      </div>

      {/* Team KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-xs">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-1">
            Total de Membros
          </span>
          <span className="text-2xl font-extrabold text-slate-900 dark:text-white font-mono">
            {members.length}
          </span>
          <span className="text-2xs text-slate-400 block mt-1">Colaboradores associados</span>
        </div>

        <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-xs">
          <span className="text-xs font-bold text-purple-700 dark:text-purple-400 uppercase tracking-wider block mb-1">
            Proprietários (Owner)
          </span>
          <span className="text-2xl font-extrabold text-purple-700 dark:text-purple-300 font-mono">
            {ownersCount}
          </span>
          <span className="text-2xs text-slate-400 block mt-1">Acesso administrativo integral</span>
        </div>

        <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-xs">
          <span className="text-xs font-bold text-emerald-700 dark:text-emerald-400 uppercase tracking-wider block mb-1">
            Programadores (Dev)
          </span>
          <span className="text-2xl font-extrabold text-emerald-700 dark:text-emerald-300 font-mono">
            {developersCount}
          </span>
          <span className="text-2xs text-slate-400 block mt-1">
            Criação de apps e testes sandbox
          </span>
        </div>

        <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-xs">
          <span className="text-xs font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider block mb-1">
            Visualizadores (Viewer)
          </span>
          <span className="text-2xl font-extrabold text-slate-700 dark:text-slate-300 font-mono">
            {viewersCount}
          </span>
          <span className="text-2xs text-slate-400 block mt-1">
            Acesso apenas de leitura e relatórios
          </span>
        </div>
      </div>

      {/* RBAC Info Card (Collapsible) */}
      {showRbacInfo && (
        <div className="bg-slate-900 text-white p-6 rounded-2xl border border-slate-800 shadow-lg space-y-4 animate-fade-in">
          <div className="flex items-center justify-between border-b border-slate-800 pb-3">
            <h3 className="text-sm font-bold flex items-center gap-2">
              <Shield className="w-4 h-4 text-primary-400" />
              <span>Matriz de Acesso e Responsabilidades por Função (RBAC)</span>
            </h3>
            <button
              onClick={() => setShowRbacInfo(false)}
              className="text-slate-400 hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
            <div className="p-4 bg-slate-950/60 rounded-xl border border-slate-800 space-y-2">
              <span className="font-bold text-purple-400 text-sm block">
                client_owner (Proprietário)
              </span>
              <p className="text-slate-300 text-2xs leading-relaxed">
                Acesso máximo à conta da empresa. Pode convidar e revogar membros, carregar saldo na
                carteira, rodar chaves de Produção e subscrever planos pagos.
              </p>
            </div>
            <div className="p-4 bg-slate-950/60 rounded-xl border border-slate-800 space-y-2">
              <span className="font-bold text-emerald-400 text-sm block">
                client_developer (Programador)
              </span>
              <p className="text-slate-300 text-2xs leading-relaxed">
                Perfil técnico para integração. Pode criar aplicações, testar rotas no Sandbox
                Tester, configurar Webhooks, origens CORS e rodar chaves de sandbox.
              </p>
            </div>
            <div className="p-4 bg-slate-950/60 rounded-xl border border-slate-800 space-y-2">
              <span className="font-bold text-slate-300 text-sm block">
                client_viewer (Visualizador)
              </span>
              <p className="text-slate-300 text-2xs leading-relaxed">
                Perfil para auditoria ou contabilidade. Pode consultar métricas de uso de APIs,
                extratos mensais e documentação técnica, sem permissão para efetuar alterações.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Team Members DataTable */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 border border-slate-200/80 dark:border-slate-800 shadow-xs space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-slate-900 dark:text-white">
            Membros da Organização
          </h2>
          <span className="text-xs text-slate-500 font-medium">
            Empresa:{' '}
            <strong className="text-slate-800 dark:text-slate-200">{client?.company_name}</strong>
          </span>
        </div>

        <DataTable
          columns={columns}
          data={members}
          isLoading={isLoading}
          emptyTitle="Nenhum membro na equipa"
          emptyDescription="Convide o primeiro colaborador da sua organização para partilhar o acesso."
        />
      </div>

      {/* Invite Member Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-slate-950/70 backdrop-blur-xs flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="bg-white dark:bg-slate-900 rounded-2xl max-w-md w-full p-6 border border-slate-200 dark:border-slate-800 shadow-2xl space-y-5">
            <div className="flex justify-between items-center pb-3 border-b border-slate-100 dark:border-slate-800">
              <h2 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-primary-500" />
                <span>Convidar Colaborador</span>
              </h2>
              <button
                onClick={() => setShowInviteModal(false)}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleInviteSubmit} className="space-y-4 text-xs">
              <div>
                <label className="block font-bold uppercase tracking-wider text-slate-500 mb-1">
                  Nome do Colaborador
                </label>
                <div className="relative">
                  <User className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    required
                    placeholder="ex: João Manuel"
                    value={inviteName}
                    onChange={(e) => setInviteName(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500 outline-hidden"
                  />
                </div>
              </div>

              <div>
                <label className="block font-bold uppercase tracking-wider text-slate-500 mb-1">
                  Email Corporativo
                </label>
                <div className="relative">
                  <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="email"
                    required
                    placeholder="colaborador@empresa.ao"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500 outline-hidden"
                  />
                </div>
              </div>

              <div>
                <label className="block font-bold uppercase tracking-wider text-slate-500 mb-1">
                  Função & Permissão
                </label>
                <div className="space-y-2">
                  <label
                    className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                      inviteRole === 'client_developer'
                        ? 'border-emerald-500 bg-emerald-50/40 dark:bg-emerald-950/30'
                        : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="role"
                      value="client_developer"
                      checked={inviteRole === 'client_developer'}
                      onChange={() => setInviteRole('client_developer')}
                      className="mt-1"
                    />
                    <div>
                      <span className="font-bold text-slate-900 dark:text-white block">
                        Programador (client_developer)
                      </span>
                      <span className="text-2xs text-slate-500 dark:text-slate-400">
                        Pode criar aplicações, testar no sandbox e gerar chaves de API.
                      </span>
                    </div>
                  </label>

                  <label
                    className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                      inviteRole === 'client_viewer'
                        ? 'border-primary-500 bg-primary-50/40 dark:bg-primary-950/30'
                        : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="role"
                      value="client_viewer"
                      checked={inviteRole === 'client_viewer'}
                      onChange={() => setInviteRole('client_viewer')}
                      className="mt-1"
                    />
                    <div>
                      <span className="font-bold text-slate-900 dark:text-white block">
                        Visualizador / Auditor (client_viewer)
                      </span>
                      <span className="text-2xs text-slate-500 dark:text-slate-400">
                        Acesso somente de leitura a métricas, documentação e extratos de faturação.
                      </span>
                    </div>
                  </label>
                </div>
              </div>

              <div className="pt-2 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowInviteModal(false)}
                  className="px-3.5 py-2 rounded-xl text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={inviteMutation.isPending || !inviteEmail}
                  className="btn-base btn-primary px-4 py-2 font-bold shadow-xs disabled:opacity-50"
                >
                  {inviteMutation.isPending ? 'A Enviar...' : 'Enviar Convite'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamManagement;
